import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const LOGO_ICON = "/logo-emblem.png";

export default function AdminResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirm) {
      toast.error("New passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err, "Could not reset the password"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#F3EDE3] flex items-center justify-center px-6" data-testid="reset-password-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md bg-white border border-[#DCD3C3] p-10"
      >
        <div className="flex items-center gap-3">
          <img src={LOGO_ICON} alt="Stud Exchange" className="w-12 h-12 rounded-full object-cover border border-[#DCD3C3]" />
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#B38728]">Admin Access</p>
            <h1 className="font-serif text-2xl">Reset Password</h1>
          </div>
        </div>

        {!token ? (
          <p className="mt-8 text-sm text-[#5C564C]" data-testid="reset-no-token">
            This reset link is missing its token — use the full link from your email.
          </p>
        ) : done ? (
          <div className="mt-8" data-testid="reset-success">
            <p className="text-sm text-[#0A0A0A] font-medium">Password updated.</p>
            <p className="text-sm text-[#5C564C] mt-2">You can log in with your new password now.</p>
            <Link to="/admin/login" data-testid="reset-login-link"
              className="mt-6 block text-center bg-[#0A0A0A] text-[#F3EDE3] py-4 text-xs font-mono uppercase tracking-[0.3em] hover:bg-[#B38728] hover:text-[#0A0A0A] transition-colors">
              Back to Login
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C564C]">New password (10+ characters)</label>
              <input
                type="password"
                required
                minLength={10}
                data-testid="reset-new-password-input"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="mt-2 w-full bg-[#F3EDE3] border border-[#DCD3C3] px-4 py-3 text-sm focus:outline-none focus:border-[#B38728] transition-colors"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C564C]">Repeat new password</label>
              <input
                type="password"
                required
                data-testid="reset-confirm-password-input"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-2 w-full bg-[#F3EDE3] border border-[#DCD3C3] px-4 py-3 text-sm focus:outline-none focus:border-[#B38728] transition-colors"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="reset-password-submit-button"
              className="w-full bg-[#0A0A0A] text-[#F3EDE3] py-4 text-xs font-mono uppercase tracking-[0.3em] hover:bg-[#B38728] hover:text-[#0A0A0A] transition-colors disabled:opacity-50"
            >
              {busy ? "Updating…" : "Set New Password"}
            </button>
          </form>
        )}

        <Link to="/admin/login" className="block mt-6 text-center text-xs text-[#5C564C] hover:text-[#B38728] transition-colors" data-testid="reset-back-link">
          ← Back to login
        </Link>
      </motion.div>
    </main>
  );
}
